// Renomeou a mesa: pergunta se o nome no meio do QR Code muda junto.
//
// O nome do QR fica gravado (`mesas.qr_nome`) justamente para o cartão já
// impresso não ficar errado sozinho. Fechar a pergunta sem responder = manter:
// é o lado seguro, o cartão que está na mesa continua batendo com o QR.
import React, { useCallback, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { gravarNomeQr } from "./api";
import { useMesaQrLigado } from "./useMesaQrLigado";

export function usePerguntaNomeQr() {
  const ligado = useMesaQrLigado();
  const [pergunta, setPergunta] = useState(null);
  // O botão e o fechamento do diálogo disparam juntos: só a 1ª resposta vale
  const respondida = useRef(false);

  /** Chame depois de gravar a mesa com o nome novo. */
  const perguntar = useCallback((mesa, nomeNovo) => {
    const antigo = mesa?.nome || `Mesa ${mesa?.numero}`;
    if (!mesa?.id || !nomeNovo || nomeNovo === antigo) return;
    // Loja sem Mesa QR e mesa que nunca teve QR gerado: não há cartão a proteger
    if (!ligado && !mesa.qr_nome) return;
    if (mesa.qr_nome && mesa.qr_nome === nomeNovo) return; // voltou ao nome do cartão
    respondida.current = false;
    setPergunta({ mesaId: mesa.id, antigo, noQr: mesa.qr_nome || antigo, novo: nomeNovo });
  }, [ligado]);

  const responder = async (atualizar) => {
    const p = pergunta;
    if (!p || respondida.current) return;
    respondida.current = true;
    setPergunta(null);
    try {
      // "Manter" só grava o nome antigo se a mesa ainda não tinha nome no QR
      await gravarNomeQr(p.mesaId, atualizar ? p.novo : p.antigo, !atualizar);
      if (atualizar) {
        toast.success(`O QR agora mostra "${p.novo}". Imprima o cartão novo — o antigo continua abrindo a mesa.`);
      }
    } catch {
      toast.error("Não consegui gravar o nome do QR Code.");
    }
  };

  const dialogo = (
    <AlertDialog open={!!pergunta} onOpenChange={(aberto) => { if (!aberto) responder(false); }}>
      <AlertDialogContent data-e2e="dialog-nome-qr">
        <AlertDialogHeader>
          <AlertDialogTitle>Atualizar o QR Code da mesa?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p>
                A mesa agora se chama <strong>{pergunta?.novo}</strong>, e o QR Code dela tem{" "}
                <strong>{pergunta?.noQr}</strong> escrito no meio.
              </p>
              <p><strong>Atualizar:</strong> o QR passa a mostrar o nome novo. Imprima o cartão de novo.</p>
              <p><strong>Manter:</strong> o cartão que já está na mesa continua igual.</p>
              <p className="text-xs">Nos dois casos o código continua abrindo esta mesa.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-e2e="nome-qr-manter" onClick={() => responder(false)}>
            Manter o QR como está
          </AlertDialogCancel>
          <AlertDialogAction data-e2e="nome-qr-atualizar" onClick={() => responder(true)}>
            Atualizar o QR
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { perguntar, dialogo };
}
